import { ReactNode, useEffect, useMemo, useState } from 'react';
import { usePrevious } from 'react-delta';
import { useRecoilValue } from 'recoil';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { activeSearchMatchCountEstimateState } from 'api/apolloClient.state';
import { abbreviateNumber } from 'common/functions';
import log from 'common/ScalyrLogger';
import { getRequestIds } from 'common/util/lrqRequest';
import Badge from 'components/common/Badge/Badge';
import Dropdown from 'components/common/Dropdown/Dropdown';
import IcomoonIcon from 'components/common/Icon/IcomoonIcon';
import Link from 'components/common/Link/Link';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import TruncateWithTooltip from 'components/common/TruncateWithTooltip/TruncateWithTooltip';
import { searchPageRemainingWidthSelector } from 'components/common/Managers/LayoutStateManager';
import {
  countPerTabRequestState,
  currentResultsTabState,
  resultsTabsState,
  resultsTabsWithFilterState,
} from 'components/common/Managers/ViewStateManager.state';
import { getResultsTabUrlState } from 'components/common/Managers/RouteStateManager.state';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { ResultsTab } from 'views/types';

import './LogResultsTabBar.scss';

type ProcessedTab = {
  active: boolean;
  badgeText?: ReactNode;
  href?: string;
  icon?: string;
  id: string;
  label: string;
  matchCount: number;
};

// Estimate of the width for an average tab
const ESTIMATED_TAB_WIDTH = 180;

const TabContent = ({ badgeText, icon, label }: Pick<ProcessedTab, 'badgeText' | 'icon' | 'label'>) => {
  const iconComponent = icon ? <IcomoonIcon name={icon} /> : null;
  const badge = badgeText ? (
    <Badge variant="info" className="mr-2">
      {badgeText}
    </Badge>
  ) : null;

  return (
    <>
      {iconComponent}
      <TruncateWithTooltip id={uniqueId('results-tab-')} text={label || ''} maxLength={20} />
      {badge}
    </>
  );
};

const Tab = ({
  active,
  badgeText,
  href,
  label,
  icon,
}: Pick<ProcessedTab, 'active' | 'badgeText' | 'href' | 'label' | 'icon'>) => (
  <div className="Tab nav-item">
    <Link href={href} className={classNames('Tab__Link', 'nav-link', active && 'active')}>
      <TabContent badgeText={badgeText} icon={icon} label={label} />
    </Link>
  </div>
);

const LogResultsTabBar = ({ resultsTabs }: { resultsTabs: ResultsTab[] }) => {
  const activeSearchMatchCountEstimate = useRecoilValue(activeSearchMatchCountEstimateState);
  const currentResultsTab = useRecoilValue(currentResultsTabState);
  const remainingWidth = useRecoilValue(searchPageRemainingWidthSelector);
  const countPerTabRequest = useRecoilValue(countPerTabRequestState);
  const resultsTabsWithFilter = useRecoilValue(resultsTabsWithFilterState);
  const getResultsTabUrl = useRecoilValue(getResultsTabUrlState);
  const requestIds = getRequestIds(countPerTabRequest);
  const { results: queryResults, errors: queryErrors } = useEventCache(requestIds);
  const [tabs, setTabs] = useState<ProcessedTab[]>([]);
  const prevTabs = usePrevious(tabs);

  useEffect(() => {
    if (queryResults.every(({ isDone }) => isDone)) {
      // Actual results count for tab starts at index 1, index 0 is for the results count without tab filters
      const [noTabFilterMatchCount, ...matchCounts] = queryResults.map(({ data }) => data.matchCount);

      const processTab = (tab: ResultsTab): ProcessedTab => {
        const matchCount = matchCounts[resultsTabsWithFilter.indexOf(tab)] ?? noTabFilterMatchCount;

        return {
          active: tab.id === currentResultsTab?.id,
          badgeText: abbreviateNumber(matchCount),
          // We build the URL even for the currently selected tab, if we pass `undefined`, the underlying
          // component will interpret it as `/`
          href: getResultsTabUrl(tab),
          icon: tab.icon,
          id: tab.id,
          label: tab.label,
          matchCount,
        };
      };
      setTabs(
        resultsTabs
          .map(processTab)
          // We always want to show the first tab regardless if it has any results
          // or not (ie: All Events tab)
          .filter(({ id, matchCount }) => id === currentResultsTab?.id || matchCount > 0),
      );
    } else {
      if (!prevTabs || prevTabs.length <= 1) {
        // To avoid the tab bar from not showing up while the query is running, we show the current or first tab only,
        // until we have results counts
        const tab = currentResultsTab ?? resultsTabs[0];
        setTabs([
          {
            active: true,
            badgeText:
              activeSearchMatchCountEstimate > 0 ? (
                abbreviateNumber(activeSearchMatchCountEstimate)
              ) : (
                <LoadingPlaceholder height={10} offsetTop={0} spinnerHeight={10} />
              ),
            icon: tab.icon,
            id: tab.id,
            label: tab.label,
            matchCount: activeSearchMatchCountEstimate,
          },
        ]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSearchMatchCountEstimate, currentResultsTab, queryResults, resultsTabs, resultsTabsWithFilter]);

  // Log errors if there was a problem getting tabs result counts
  useEffect(() => {
    const actualErrors = queryErrors.filter(Boolean);
    if (actualErrors.length > 0) {
      log.error('Error getting results match count for view tabs counts', { errors: actualErrors });
    }
  }, [queryErrors]);

  const { tabsToHide, tabsToShow } = useMemo(() => {
    const maxTabsBeforeOverflow = Math.floor(remainingWidth / ESTIMATED_TAB_WIDTH) - 1; // We have to account for `More` button
    const tabsToShow = tabs.slice(0, maxTabsBeforeOverflow);
    const tabsOverflow = tabs.slice(maxTabsBeforeOverflow);

    return {
      tabsToHide: tabsOverflow.map(({ active, badgeText, href, icon, label }) => ({
        active,
        href,
        children: <TabContent badgeText={badgeText} label={label} icon={icon} />,
      })),
      tabsToShow,
    };
  }, [remainingWidth, tabs]);

  return (
    <div className="LogResultsTabBar">
      <div className="TabList__Tabs nav" role="tablist">
        {tabsToShow.map(({ active, badgeText, href, icon, id, label }) => (
          <Tab key={id} badgeText={badgeText} href={href} icon={icon} active={active} label={label} />
        ))}
      </div>
      {tabsToHide.length > 0 && (
        <div>
          <Dropdown
            className="LogResultsTabBar__TabsOverflowDropdown"
            isNav={true}
            itemAs={Link}
            rightIcon={<KeyboardArrowDownIcon />}
            sections={[tabsToHide]}
          >
            {tabsToHide.find(({ active }) => active)?.children || 'More'}
          </Dropdown>
        </div>
      )}
    </div>
  );
};

const LogResultsTabBarWrapper = () => {
  const resultsTabs = useRecoilValue(resultsTabsState);

  if (resultsTabs.length === 0) {
    return <></>;
  }

  return <LogResultsTabBar resultsTabs={resultsTabs} />;
};

export default LogResultsTabBarWrapper;
